/**
 * Hot-path CheckInOut history + bulk fan-out limits.
 *
 * Desk create* and scanQr only need last-state / require-checkout / rapid-dedupe
 * from the newest rows. Scan-limit enforcement must use count(), not this window,
 * so a high numberOfScans (or a long IN/OUT history) stays correct.
 *
 * 32 is enough for a multi-day IN/OUT pair per day plus typical zone interleaving.
 * If this zone is missing from the window, callers issue a bounded zone fallback.
 */

export const CHECK_IN_OUT_HISTORY_TAKE = 32;
export const CHECK_IN_OUT_HISTORY_TAKE_MAX = 200;

export const DEFAULT_BULK_CHECKIN_CONCURRENCY = 8;
export const BULK_CHECKIN_CONCURRENCY_MAX = 32;

export const CHECKS_IN_OUT_HOT_PATH_SELECT = {
  id: true,
  type: true,
  time: true,
  createdAt: true,
  zoneId: true,
  message: true,
} as const;

function readBoundedPositiveInt(
  raw: string | undefined,
  fallback: number,
  max: number
): number {
  const parsed = raw ? Number(raw) : NaN;
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return fallback;
  }
  return Math.min(parsed, max);
}

export function readCheckInOutHistoryTake(
  env: NodeJS.ProcessEnv = process.env
): number {
  return readBoundedPositiveInt(
    env.CHECK_IN_OUT_HISTORY_TAKE,
    CHECK_IN_OUT_HISTORY_TAKE,
    CHECK_IN_OUT_HISTORY_TAKE_MAX
  );
}

export function readBulkCheckinConcurrencyFromEnv(
  env: NodeJS.ProcessEnv = process.env
): number {
  return readBoundedPositiveInt(
    env.CHECK_IN_OUT_BULK_CONCURRENCY,
    DEFAULT_BULK_CHECKIN_CONCURRENCY,
    BULK_CHECKIN_CONCURRENCY_MAX
  );
}

export function checksInOutHotPathQuery(take = readCheckInOutHistoryTake()) {
  return {
    select: CHECKS_IN_OUT_HOT_PATH_SELECT,
    orderBy: { time: "desc" as const },
    take,
  };
}

export function checkInCountWhere(orderItemId: string, zoneId?: string) {
  return {
    orderItemId,
    type: "CHECK_IN" as const,
    ...(zoneId ? { zoneId } : {}),
  };
}

export function needsZoneHistoryFallback(
  checksInOut: Array<{ zoneId?: string | null }>,
  zoneId?: string
): boolean {
  if (!zoneId) return false;
  return !checksInOut.some((row) => row.zoneId === zoneId);
}

export function hasCheckInInMemory(
  checksInOut: Array<{ type: string; zoneId?: string | null }>,
  zoneId?: string
): boolean {
  return checksInOut.some((row) => {
    if (row.type !== "CHECK_IN") return false;
    return zoneId ? row.zoneId === zoneId : true;
  });
}

export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  if (items.length === 0) {
    return results;
  }

  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  let nextIndex = 0;

  const runWorker = async () => {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) {
        return;
      }
      try {
        const value = await mapper(items[index] as T, index);
        results[index] = { status: "fulfilled", value };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return results;
}
